import { Table } from "@tanstack/react-table";
import React, { useEffect } from "react";
import { MdKeyboardDoubleArrowLeft } from "react-icons/md";
import { MdKeyboardDoubleArrowRight } from "react-icons/md";
import { MdKeyboardArrowLeft } from "react-icons/md";
import { MdKeyboardArrowRight } from "react-icons/md";

interface PaginationProps {
  table: Table<any>;
  sizes: number[];
  isLoading: boolean;
  total: number;
}
export const Pagination = ({
  table,
  sizes,
  total,
  isLoading,
}: PaginationProps) => {
  const [pageValue, setPageValue] = React.useState<string>(
    table.getState().pagination.pageIndex + 1 + ""
  );

  const manualSetPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageValue(e.target.value);
    const page = Number(e.target.value);
    if (isNaN(page)) return;
    if (page < 0) {
      table.setPageIndex(0);
    } else if (page >= table.getPageCount()) {
      table.setPageIndex(page - 1);
    } else {
      table.setPageIndex(3);
      table.setPageIndex(page - 1);
    }
  };

  const refreshPageInputValue = () => {
    if (pageValue != table.getState().pagination.pageIndex + 1 + "")
      setPageValue(table.getState().pagination.pageIndex + 1 + "");
  };

  const onSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    table.setPageSize(parseInt(e.target.value, 10));
  };

  useEffect(() => {
    if (Number(pageValue) > table.getPageCount()) {
      setPageValue("1");
      table.setPageIndex(0);
    }
  }, [table.getPageCount()]);

  useEffect(() => {
    refreshPageInputValue();
  }, [table.getState().pagination.pageIndex, table.getPageCount()]);

  return (
    <footer className="pagination flex flex-wrap items-center gap-2 rounded-b-xl justify-between bg-gray-200 p-2 dark:bg-slate-700">
      <div className="text-sm">
        <select
          value={table.getState().pagination.pageSize}
          onChange={onSelectChange}
          className="dark:bg-slate-800 pl-2 p-1 pr-7"
        >
          {sizes.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <span className="ml-1">
          {" "}
          {}z {total}{" "}
          <span className="md:inline-block hidden">elementów na stronę</span>
        </span>
      </div>
      <div className="flex text-sm">
        {isLoading && <TableLoader />}
        <TablePaginationButton
          disabled={!table.getCanPreviousPage()}
          onClick={() => table.setPageIndex(0)}
        >
          <MdKeyboardDoubleArrowLeft size={26} />
        </TablePaginationButton>
        <TablePaginationButton
          disabled={!table.getCanPreviousPage()}
          onClick={table.previousPage}
        >
          <MdKeyboardArrowLeft size={26} />
        </TablePaginationButton>
        <div className="text-sm flex items-center">
          <input
            value={pageValue}
            onBlur={refreshPageInputValue}
            onChange={manualSetPage}
            className="w-9 p-1 text-center mx-1 dark:bg-slate-800"
          />
          <span className="md:inline-block hidden mr-1">strona z </span>
          <span className="md:hidden mr-1">/</span>
          {table.getPageCount()}
        </div>
        <TablePaginationButton
          disabled={!table.getCanNextPage()}
          onClick={table.nextPage}
        >
          <MdKeyboardArrowRight size={26} />
        </TablePaginationButton>
        <TablePaginationButton
          disabled={!table.getCanNextPage()}
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
        >
          <MdKeyboardDoubleArrowRight size={26} />
        </TablePaginationButton>
      </div>
    </footer>
  );
};

interface TablePaginationButtonProps {
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const TablePaginationButton = ({
  disabled,
  onClick,
  children,
}: TablePaginationButtonProps) => {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      type="button"
      className="p-1 cursor-pointer hover:text-slate-500 dark:text-slate-300 disabled:text-slate-400 disabled:cursor-not-allowed disabled:dark:text-slate-500"
    >
      {children}
    </button>
  );
};

const TableLoader = () => {
  return (
    <div className="flex items-center mr-2">
      <div
        className="inline-block h-4 w-4 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
        role="status"
      >
        <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
          Loading...
        </span>
      </div>
    </div>
  );
};
